import { useEffect, useState, FormEvent } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, brandStats } from '@/mocks/testimonials';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 | JM Comic – 官方入口与完整使用指南';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u3nvl3pcjqs2fcsleg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).filter(([k]) => k !== 'phone_alt').map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'JM Comic',
    url: window.location.origin,
    description:
      'JM Comic 是广受欢迎的漫画阅读平台，提供海量正版与同人漫画资源。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@jmcomic.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      <Breadcrumb items={[{ label: '关于我们' }]} />

      <section className="py-10 md:py-14">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              关于 JM Comic
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              JM Comic 致力于为全球漫画爱好者打造最优质的在线阅读平台，
              汇聚海量正版漫画与同人创作，让每一位读者都能找到属于自己的漫画世界。
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
            {brandStats.map((stat) => (
              <div
                key={stat.label}
                className="bg-background-100/70 rounded-xl p-5 text-center border border-background-200/60"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mx-auto mb-3">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
              品牌故事
            </h2>
            <div className="bg-background-100/70 rounded-xl p-6 md:p-8 border border-background-200/60 leading-relaxed text-foreground-700">
              <p className="mb-4">
                JM Comic 诞生于一群漫画爱好者对优质阅读体验的共同追求。我们深知，每一位漫画读者都渴望在一个界面友好、资源丰富、响应迅速的平台上，尽情享受漫画带来的乐趣。
              </p>
              <p className="mb-4">
                自成立以来，JM Comic 始终坚持"读者至上，内容为王"的核心理念。我们与国内外众多漫画出版社和独立创作者建立了紧密的合作关系，确保平台上的每一部作品都经过正版授权，为创作者和读者创造一个相互尊重、共同成长的良好生态。
              </p>
              <p className="mb-4">
                JM Comic 不仅是一个漫画阅读工具，更是一个充满活力的漫画爱好者社区。在这里，读者可以交流心得、分享推荐，创作者可以获得真实的反馈和支持。我们的智能推荐系统会根据每位读者的阅读偏好，精准推荐可能感兴趣的作品，让发现好漫画变得更加简单。
              </p>
              <p>
                未来，JM Comic 将继续深耕漫画领域，不断优化阅读体验，拓展内容资源，为全球漫画爱好者带来更多惊喜。感谢每一位选择 JM Comic 的用户，你们的支持是我们前进的最大动力。
              </p>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-100/70 rounded-xl p-5 border border-background-200/60"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-foreground-900 text-sm">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${
                          i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
              联系我们
            </h2>
            <div className="max-w-xl mx-auto">
              <div className="bg-background-100/70 rounded-xl p-6 md:p-8 border border-background-200/60 mb-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 text-primary-600 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-600">support@jmcomic.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 text-primary-600 rounded-lg flex-shrink-0">
                      <i className="ri-customer-service-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">在线客服</div>
                      <div className="text-sm text-foreground-600">App内「我的-帮助与反馈」提交工单</div>
                    </div>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    您的姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入咨询主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <div className="honeypot-field" aria-hidden="true">
                  <label htmlFor="phone_alt" className="sr-only-hp">备用电话</label>
                  <input
                    type="text"
                    id="phone_alt"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    aria-hidden="true"
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
                >
                  {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="text-center py-2 px-4 bg-green-50 text-green-700 rounded-lg text-sm">
                    <i className="ri-check-line mr-1"></i>
                    留言发送成功！我们会尽快回复您。
                  </div>
                )}

                {formStatus === 'error' && formError && (
                  <div className="text-center py-2 px-4 bg-red-50 text-red-700 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}